import React from "react";
import { Text as RNText, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import Spinner from "../../../components/shared/Spinner";
import { View } from "../../../components/shared/Themed";

const EVENT_ICON_MAP: Record<string, string> = {
  GATEIN: "log-in-outline",
  GATEOUT: "log-out-outline",
  LOAD: "arrow-up-circle-outline",
  DISCHARGE: "arrow-down-circle-outline",
  DEHIRE: "checkmark-done-circle-outline",
  ONBOARDVESSEL: "boat-outline",
  IMPORTAVAILABLE: "checkmark-circle-outline",
  RAILDEPARTURE: "train-outline",
  RAILARRIVAL: "train-outline",
};

const normalizeEventKey = (eventType?: string) =>
  (eventType || "").toUpperCase().replace(/[\s_]/g, "");

const getEventIcon = (eventType?: string): any =>
  EVENT_ICON_MAP[normalizeEventKey(eventType)] || "ellipse-outline";

const toDate = (value: any): Date | null => {
  if (!value) return null;
  const raw =
    typeof value === "object" && "$date" in value ? value.$date : value;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const formatEventDateTime = (value: any) => {
  const d = toDate(value);
  if (!d) return "—";
  const date = `${("0" + d.getDate()).slice(-2)}/${("0" + (d.getMonth() + 1)).slice(-2)}/${d.getFullYear()}`;
  const time = `${("0" + d.getHours()).slice(-2)}:${("0" + d.getMinutes()).slice(-2)}`;
  return `${date} ${time}`;
};

const formatDateOnly = (value: any) => {
  const d = toDate(value);
  if (!d) return null;
  return `${("0" + d.getDate()).slice(-2)}/${("0" + (d.getMonth() + 1)).slice(-2)}/${d.getFullYear()}`;
};

// eventLocode arrives with stray trailing/leading whitespace in real
// payloads ("HUTCHISON PORTS - FISHERMAN ISLAND ") — trim before display.
const getLocationLabel = (evt: any): string | null => {
  if (evt.location?.commonName) {
    return evt.location.countryCode
      ? `${evt.location.commonName} · ${evt.location.countryCode}`
      : evt.location.commonName;
  }
  if (evt.eventLocode) return evt.eventLocode.trim();
  if (evt.facilityId) return evt.facilityId;
  return null;
};

const hasActiveHold = (holds: any): boolean => {
  if (!holds) return false;
  return Object.values(holds).some(
    (h: any) => h?.Status && h.Status !== "Released" && h.Status !== null,
  );
};

function DetailRow({
  label,
  value,
  isDark,
}: {
  label: string;
  value: string;
  isDark: boolean;
}) {
  return (
    <View style={{ flexDirection: "row", marginTop: 3, gap: 4 }}>
      <RNText
        style={{
          fontSize: 11,
          color: isDark ? "#9CA3AF" : "#94A3B8",
          fontWeight: "600",
        }}
      >
        {label}:
      </RNText>
      <RNText
        style={{
          fontSize: 11,
          color: isDark ? "#D1D5DB" : "#475569",
          flexShrink: 1,
        }}
      >
        {value}
      </RNText>
    </View>
  );
}

// ── Status Summary Card ────────────────────────────────
// Sits above the timeline. Answers "where is this container right
// now?" without scrolling through every historical push — pulled
// from the most recent event by eventOccurred.
function LiveStatusSummary({
  latest,
  colors,
  isDark,
}: {
  latest: any;
  colors: any;
  isDark: boolean;
}) {
  if (!latest) return null;

  const vessel = latest.equipmentJourney?.VesselVoyage;
  const loadPort = latest.equipmentJourney?.LoadPort?.Locode;
  const dischargePort = latest.equipmentJourney?.DischargePort?.Locode;
  const eta = formatDateOnly(vessel?.EstimatedArrival);
  const locationLabel = getLocationLabel(latest);
  const onHold = hasActiveHold(latest.holds);

  return (
    <View
      style={{
        backgroundColor: isDark ? colors.primary + "1A" : "#EFF6FF",
        borderRadius: 10,
        borderWidth: 1,
        borderColor: isDark ? colors.primary + "44" : "#DBEAFE",
        padding: 12,
        marginBottom: 14,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          marginBottom: 6,
        }}
      >
        <Ionicons
          name={getEventIcon(latest.eventType)}
          size={16}
          color={isDark ? colors.primary : "#2F95DC"}
        />
        <RNText
          style={{
            fontSize: 13,
            fontWeight: "700",
            color: isDark ? colors.text : "#0F172A",
          }}
        >
          {latest.eventType}
        </RNText>
        <RNText
          style={{
            fontSize: 11,
            color: isDark ? colors.textSecondary : "#64748B",
          }}
        >
          · {formatEventDateTime(latest.eventOccurred)}
        </RNText>
      </View>

      {locationLabel ? (
        <DetailRow isDark={isDark} label="Location" value={locationLabel} />
      ) : null}
      {vessel?.Name ? (
        <DetailRow
          isDark={isDark}
          label="Vessel"
          value={`${vessel.Name}${vessel.Voyage ? ` · Voyage ${vessel.Voyage}` : ""}`}
        />
      ) : null}
      {loadPort || dischargePort ? (
        <DetailRow
          isDark={isDark}
          label="Route"
          value={`${loadPort || "—"} → ${dischargePort || "—"}`}
        />
      ) : null}
      {eta ? <DetailRow isDark={isDark} label="ETA Port" value={eta} /> : null}
      {onHold ? (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginTop: 5,
          }}
        >
          <Ionicons name="alert-circle-outline" size={12} color="#F59E0B" />
          <RNText style={{ fontSize: 11, color: "#F59E0B", fontWeight: "600" }}>
            Hold active
          </RNText>
        </View>
      ) : null}
    </View>
  );
}

export function OneStopLiveUpdatesTimeline({
  updates,
  loading,
  error,
  page,
  totalPages,
  onPageChange,
  colors,
  isDark,
}: {
  updates: Array<any>;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  colors: any;
  isDark: boolean;
}) {
  if (loading) {
    return (
      <View style={{ paddingVertical: 20, alignItems: "center" }}>
        <Spinner size="small" color={isDark ? colors.primary : "#2F95DC"} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={{ paddingVertical: 16, alignItems: "center" }}>
        <RNText style={{ color: "#EF4444", fontSize: 13 }}>{error}</RNText>
      </View>
    );
  }

  if (updates.length === 0) {
    return (
      <View style={{ paddingVertical: 24, alignItems: "center", gap: 8 }}>
        <Ionicons name="wifi-off" size={28} color="#CBD5E1" />
        <RNText style={{ color: "#94A3B8", fontSize: 13 }}>
          No OneStop live updates yet for this container
        </RNText>
      </View>
    );
  }

  // `updates` is already sorted -eventOccurred (newest first) by the
  // API, so index 0 is the latest known status.
  const latest = updates[0];

  // Detect a vessel change between consecutive events (transshipment)
  // so it can be called out inline rather than buried as two
  // near-identical "On Board Vessel" rows.
  const vesselChangedAt = (idx: number) => {
    const current = updates[idx]?.equipmentJourney?.VesselVoyage?.Name;
    const next = updates[idx + 1]?.equipmentJourney?.VesselVoyage?.Name;
    return !!current && !!next && current !== next;
  };

  return (
    <View style={{ paddingTop: 8 }}>
      <RNText
        style={{
          fontSize: 13,
          fontWeight: "700",
          color: isDark ? colors.text : "#374151",
          marginBottom: 10,
        }}
      >
        OneStop Live Updates
      </RNText>

      <LiveStatusSummary latest={latest} colors={colors} isDark={isDark} />

      {updates.map((evt: any, idx: number) => {
        const locationLabel = getLocationLabel(evt);
        const vessel = evt.equipmentJourney?.VesselVoyage;
        const loadPort = evt.equipmentJourney?.LoadPort?.Locode;
        const dischargePort = evt.equipmentJourney?.DischargePort?.Locode;
        const vesselEta = formatDateOnly(vessel?.EstimatedArrival);
        const vesselEtd = formatDateOnly(vessel?.EstimatedDeparture);
        const onHold = hasActiveHold(evt.holds);
        const showTransshipmentFlag = vesselChangedAt(idx);

        return (
          <View
            key={evt._id?.$oid || evt._id || idx}
            style={{
              flexDirection: "row",
              gap: 10,
              paddingVertical: 10,
              borderBottomWidth: idx === updates.length - 1 ? 0 : 1,
              borderBottomColor: isDark ? colors.background : "#F1F5F9",
            }}
          >
            <View
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                backgroundColor: isDark ? colors.primary + "33" : "#E1F0FA",
                alignItems: "center",
                justifyContent: "center",
                marginTop: 2,
              }}
            >
              <Ionicons
                name={getEventIcon(evt.eventType)}
                size={16}
                color={isDark ? colors.primary : "#2F95DC"}
              />
            </View>

            <View style={{ flex: 1 }}>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <RNText
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: isDark ? colors.text : "#0F172A",
                  }}
                >
                  {evt.eventType}
                </RNText>
                <RNText
                  style={{
                    fontSize: 11,
                    color: isDark ? colors.textSecondary : "#94A3B8",
                  }}
                >
                  {formatEventDateTime(evt.eventOccurred)}
                </RNText>
              </View>

              {locationLabel ? (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    marginTop: 3,
                  }}
                >
                  <Ionicons
                    name="location-outline"
                    size={11}
                    color={isDark ? colors.textSecondary : "#6B7280"}
                  />
                  <RNText
                    style={{
                      fontSize: 12,
                      color: isDark ? colors.textSecondary : "#6B7280",
                      flexShrink: 1,
                    }}
                  >
                    {locationLabel}
                  </RNText>
                </View>
              ) : null}

              {vessel?.Name ? (
                <DetailRow
                  isDark={isDark}
                  label="Vessel"
                  value={`${vessel.Name}${vessel.Voyage ? ` · Voyage ${vessel.Voyage}` : ""}`}
                />
              ) : null}

              {loadPort || dischargePort ? (
                <DetailRow
                  isDark={isDark}
                  label="Route"
                  value={`${loadPort || "—"} → ${dischargePort || "—"}`}
                />
              ) : null}

              {vesselEtd ? (
                <DetailRow isDark={isDark} label="ETD" value={vesselEtd} />
              ) : null}
              {vesselEta ? (
                <DetailRow isDark={isDark} label="ETA Port" value={vesselEta} />
              ) : null}

              {onHold ? (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    marginTop: 4,
                  }}
                >
                  <Ionicons
                    name="alert-circle-outline"
                    size={12}
                    color="#F59E0B"
                  />
                  <RNText
                    style={{
                      fontSize: 11,
                      color: "#F59E0B",
                      fontWeight: "600",
                    }}
                  >
                    Hold active
                  </RNText>
                </View>
              ) : null}

              {showTransshipmentFlag ? (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    marginTop: 4,
                  }}
                >
                  <Ionicons
                    name="swap-horizontal-outline"
                    size={12}
                    color="#8B5CF6"
                  />
                  <RNText
                    style={{
                      fontSize: 11,
                      color: "#8B5CF6",
                      fontWeight: "600",
                    }}
                  >
                    Transshipped from previous vessel
                  </RNText>
                </View>
              ) : null}

              {evt.source ? (
                <RNText
                  style={{
                    fontSize: 10,
                    color: "#94A3B8",
                    marginTop: 4,
                    textTransform: "uppercase",
                  }}
                >
                  {evt.source === "WEBHOOK" ? "Live push" : "Polled"}
                </RNText>
              ) : null}
            </View>
          </View>
        );
      })}

      {totalPages > 1 && (
        <View
          style={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: 16,
            paddingTop: 12,
          }}
        >
          <TouchableOpacity
            disabled={page <= 1}
            onPress={() => onPageChange(page - 1)}
            style={{ opacity: page <= 1 ? 0.3 : 1 }}
          >
            <Ionicons
              name="chevron-back"
              size={18}
              color={isDark ? colors.primary : "#2F95DC"}
            />
          </TouchableOpacity>
          <RNText
            style={{
              fontSize: 12,
              color: isDark ? colors.textSecondary : "#6B7280",
            }}
          >
            {page} / {totalPages}
          </RNText>
          <TouchableOpacity
            disabled={page >= totalPages}
            onPress={() => onPageChange(page + 1)}
            style={{ opacity: page >= totalPages ? 0.3 : 1 }}
          >
            <Ionicons
              name="chevron-forward"
              size={18}
              color={isDark ? colors.primary : "#2F95DC"}
            />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}
